import { useEffect, useMemo, useState } from "react";
import { CheckCircle2, Copy, Eye, EyeOff, KeyRound, Link2, RefreshCw, Wrench, XCircle } from "lucide-react";
import { toast } from "sonner";

import { Layout } from "@/components/Layout";
import { HoardexaLoader } from "@/components/HoardexaLoader";
import { SegmentedTabs } from "@/components/UXPrimitives";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import api, { formatApiError } from "@/lib/api";
import { inr } from "@/lib/format";

const num = (v) => Number(String(v || "").replace(/[^\d.]/g, "") || 0);
const asArray = (v) => Array.isArray(v) ? v : [];
const visibilityOptions = [
  { value: "available", label: "Available" },
  { value: "occupied", label: "Occupied" },
  { value: "maintenance", label: "Under maintenance" },
  { value: "hidden", label: "Hidden" },
];

function Pill({ children, tone = "slate" }) {
  const cls = {
    slate: "border-slate-200 bg-slate-50 text-slate-700",
    blue: "border-blue-200 bg-blue-50 text-blue-700",
    green: "border-emerald-200 bg-emerald-50 text-emerald-700",
    amber: "border-amber-200 bg-amber-50 text-amber-700",
    red: "border-red-200 bg-red-50 text-red-700",
  }[tone] || "border-slate-200 bg-slate-50 text-slate-700";
  return <span className={`inline-flex items-center rounded-md border px-2 py-1 text-xs font-bold ${cls}`}>{children}</span>;
}

function statusTone(status) {
  if (status === "approved" || status === "active") return "green";
  if (status === "rejected" || status === "revoked") return "red";
  if (status === "pending" || status === "offered") return "amber";
  return "slate";
}

export function PartnerNetwork() {
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");
  const [activeFlow, setActiveFlow] = useState("share");
  const [settings, setSettings] = useState(null);
  const [hostPartnerships, setHostPartnerships] = useState([]);
  const [ownerPartnerships, setOwnerPartnerships] = useState([]);
  const [hostListings, setHostListings] = useState([]);
  const [ownerListings, setOwnerListings] = useState([]);
  const [selectedOwnerListingIds, setSelectedOwnerListingIds] = useState([]);
  const [bulkVisibilityStatus, setBulkVisibilityStatus] = useState("available");
  const [hostUsages, setHostUsages] = useState([]);
  const [ownerUsages, setOwnerUsages] = useState([]);
  const [connectForm, setConnectForm] = useState({ host_email: "", access_code: "" });

  const load = () => {
    setLoading(true);
    Promise.all([
      api.get("/partner-network/settings", { skipGlobalLoader: true }),
      api.get("/partner-network/partnerships?side=host", { skipGlobalLoader: true }),
      api.get("/partner-network/partnerships?side=owner", { skipGlobalLoader: true }),
      api.get("/partner-network/listings?side=host", { skipGlobalLoader: true }),
      api.get("/partner-network/listings?side=owner", { skipGlobalLoader: true }),
      api.get("/partner-network/usages?side=host", { skipGlobalLoader: true }),
      api.get("/partner-network/usages?side=owner", { skipGlobalLoader: true }),
    ])
      .then(([settingsRes, hostPartRes, ownerPartRes, hostListRes, ownerListRes, hostUsageRes, ownerUsageRes]) => {
        setSettings(settingsRes.data || {});
        setHostPartnerships(asArray(hostPartRes.data));
        setOwnerPartnerships(asArray(ownerPartRes.data));
        setHostListings(asArray(hostListRes.data));
        setOwnerListings(asArray(ownerListRes.data));
        setHostUsages(asArray(hostUsageRes.data));
        setOwnerUsages(asArray(ownerUsageRes.data));
      })
      .catch((e) => toast.error(formatApiError(e.response?.data?.detail) || e.message))
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  const offeredListingIds = hostListings.filter((l) => l.status === "offered").map((l) => l.id);
  const pendingOwnerUsages = ownerUsages.filter((u) => u.status === "pending");
  const sharedInventoryRows = useMemo(() => groupOwnerListings(ownerListings), [ownerListings]);
  const allSharedRowListingIds = sharedInventoryRows.flatMap((row) => row.listing_ids);
  const selectedSharedRows = sharedInventoryRows.filter((row) => row.listing_ids.some((id) => selectedOwnerListingIds.includes(id)));
  const canUsePartnerInventory = !!settings?.enabled && !!settings?.use_partner_inventory_enabled;
  const canShareInventory = !!settings?.enabled && !!settings?.share_inventory_enabled;
  const flowTabs = useMemo(() => [
    canUsePartnerInventory && { value: "share", label: "Use partner inventory", meta: hostPartnerships.filter((p) => p.status === "pending").length + offeredListingIds.length + hostUsages.filter((u) => u.status === "pending").length },
    canShareInventory && { value: "use", label: "Share my inventory", meta: ownerPartnerships.filter((p) => p.status === "pending").length + pendingOwnerUsages.length },
  ].filter(Boolean), [canShareInventory, canUsePartnerInventory, hostPartnerships, hostUsages, offeredListingIds.length, ownerPartnerships, pendingOwnerUsages.length]);

  useEffect(() => {
    if (!settings || flowTabs.some((tab) => tab.value === activeFlow)) return;
    if (flowTabs[0]) setActiveFlow(flowTabs[0].value);
  }, [activeFlow, flowTabs, settings]);

  async function updateSettings(patch) {
    setBusy("settings");
    try {
      const { data } = await api.put("/partner-network/settings", patch);
      setSettings(data || {});
      if (Object.prototype.hasOwnProperty.call(patch, "enabled")) {
        window.dispatchEvent(new CustomEvent("partner-network-sidebar-setting-changed", { detail: { showInSidebar: !!patch.enabled } }));
      }
      if (patch.rotate_code && data?.access_code) toast.success("New code generated. Old code is no longer valid.");
      else toast.success("Partner Network settings updated");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setBusy("");
    }
  }

  async function connectPartner() {
    setBusy("connect");
    try {
      await api.post("/partner-network/connect", connectForm);
      toast.success("Partner connection created");
      setConnectForm({ host_email: "", access_code: "" });
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setBusy("");
    }
  }

  async function decideListings(status, ids = offeredListingIds) {
    if (ids.length === 0) return;
    setBusy(`listings-${status}`);
    try {
      await api.post("/partner-network/listings/decision", { listing_ids: ids, status });
      toast.success(`Listings ${status}`);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setBusy("");
    }
  }

  async function decidePartnership(id, status) {
    setBusy(`partnership-${id}-${status}`);
    try {
      const { data } = await api.post(`/partner-network/partnerships/${id}/decision`, { status });
      toast.success(`Partner connection ${data?.status || status}`);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setBusy("");
    }
  }

  async function updatePartnershipCommission(id, commissionPct) {
    setBusy(`partnership-${id}-commission`);
    try {
      await api.post(`/partner-network/partnerships/${id}/decision`, { status: "approved", commission_pct: num(commissionPct) });
      toast.success("Advertiser commission updated");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setBusy("");
    }
  }

  async function updateListingCommission(id, commissionPct) {
    setBusy(`listing-${id}-commission`);
    try {
      await api.patch(`/partner-network/listings/${id}`, { commission_pct: num(commissionPct) });
      toast.success("Hoarding commission updated");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setBusy("");
    }
  }

  async function updateSelectedVisibility() {
    if (selectedOwnerListingIds.length === 0) {
      toast.error("Select at least one shared hoarding");
      return;
    }
    setBusy("owner-visibility");
    try {
      const { data } = await api.patch("/partner-network/visibility", {
        listing_ids: selectedOwnerListingIds,
        status: bulkVisibilityStatus,
      });
      setOwnerListings(asArray(data));
      setSelectedOwnerListingIds([]);
      toast.success("Partner visibility updated");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setBusy("");
    }
  }

  async function decideUsage(id, status) {
    setBusy(`${id}-${status}`);
    try {
      await api.post(`/partner-network/usages/${id}/decision`, { status });
      toast.success(`Usage ${status}`);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setBusy("");
    }
  }

  const actions = <Button variant="outline" onClick={load} disabled={loading}><RefreshCw size={15} className={loading ? "animate-spin" : ""} /> Refresh</Button>;

  return (
    <Layout title="Partner Network" subtitle="Share and approve inventory across advertiser accounts" actions={actions} mainClassName="bg-[#F8FAFC] p-6">
      {loading ? <HoardexaLoader label="Loading Partner Network" /> : (
        <div className="mx-auto grid max-w-[1500px] gap-5">
          {flowTabs.length === 0 ? (
            <div className="rounded-lg border border-slate-200 bg-white p-6 text-center shadow-sm">
              <h2 className="text-base font-black text-slate-950">Partner Network is not turned on</h2>
              <p className="mt-2 text-sm text-slate-500">Turn it on from Business Settings and choose whether this business wants to share inventory, use partner inventory, or both.</p>
            </div>
          ) : flowTabs.length > 1 ? (
          <div className="flex flex-col gap-3 rounded-lg border border-slate-200 bg-white p-4 shadow-sm sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h2 className="text-base font-black text-slate-950">Choose Partner Network Flow</h2>
              <p className="mt-1 text-sm text-slate-500">Only the workflows enabled in Business Settings are shown here.</p>
            </div>
            <div className="hx-tabs-scroll">
              <SegmentedTabs active={activeFlow} onChange={setActiveFlow} tabs={flowTabs} />
            </div>
          </div>
          ) : null}

          {activeFlow === "share" && canUsePartnerInventory && (
          <section className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div>
                <h2 className="text-base font-black text-slate-950">Partner Inventory Access</h2>
                <p className="mt-1 text-sm text-slate-500">Share your access code with inventory owners and approve the sites they offer.</p>
              </div>
            </div>
            <div className="mt-4 grid gap-3 md:grid-cols-[1fr_1fr_auto]">
              <label className="space-y-1">
                <span className="text-xs font-bold text-slate-600">Default display commission %</span>
                <Input
                  type="number"
                  min="0"
                  max="100"
                  value={settings?.default_commission_pct ?? 0}
                  onChange={(e) => setSettings((p) => ({ ...p, default_commission_pct: e.target.value }))}
                  onBlur={() => updateSettings({ default_commission_pct: num(settings?.default_commission_pct) })}
                />
              </label>
              <label className="space-y-1">
                <span className="text-xs font-bold text-slate-600">Current code</span>
                <div className="flex gap-2">
                  <Input readOnly value={settings?.access_code || "No code generated"} />
                  {settings?.access_code && <Button variant="outline" onClick={() => navigator.clipboard?.writeText(settings.access_code)}><Copy size={15} /></Button>}
                </div>
              </label>
              <div className="flex items-end gap-2">
                <Button onClick={() => updateSettings({ rotate_code: true, enabled: true, use_partner_inventory_enabled: true, default_commission_pct: num(settings?.default_commission_pct) })} disabled={busy === "settings"}><KeyRound size={15} /> {settings?.access_code ? "Regenerate Code" : "Generate Code"}</Button>
              </div>
            </div>
          </section>
          )}

          {activeFlow === "use" && canShareInventory && (
          <div className="grid gap-5">
            <section className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
              <h2 className="text-base font-black text-slate-950">Connect To A Host</h2>
              <p className="mt-1 text-sm text-slate-500">Use the host advertiser email and Partner Network code they shared with you. Share selected hoardings from the Hoardings page.</p>
              <div className="mt-4 grid gap-3 md:grid-cols-[1fr_1fr_auto]">
                <Input placeholder="host@email.com" value={connectForm.host_email} onChange={(e) => setConnectForm((p) => ({ ...p, host_email: e.target.value }))} />
                <Input placeholder="Access code" value={connectForm.access_code} onChange={(e) => setConnectForm((p) => ({ ...p, access_code: e.target.value }))} />
                <Button onClick={connectPartner} disabled={busy === "connect"}><Link2 size={15} /> Connect</Button>
              </div>
              <div className="mt-4 grid gap-2">
                {ownerPartnerships.map((p) => (
                  <div key={p.id} className="flex items-center justify-between rounded-lg border border-slate-200 p-3">
                    <div>
                      <div className="font-bold text-slate-950">{p.host_name}</div>
                      <div className="text-xs text-slate-500">{p.host_email}</div>
                    </div>
                    <Pill tone={statusTone(p.status)}>{p.status}</Pill>
                  </div>
                ))}
              </div>
            </section>
          </div>
          )}

          {activeFlow === "share" && canUsePartnerInventory && (
          <section className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
            <h2 className="text-base font-black text-slate-950">Received Partner Connections</h2>
            <p className="mt-1 text-sm text-slate-500">Advertisers who used your email and code will appear here. Their offered hoardings appear in the approval queue below.</p>
            <PartnershipGrid partnerships={hostPartnerships} side="host" busy={busy} onDecision={decidePartnership} onCommissionChange={updatePartnershipCommission} />
          </section>
          )}

          {activeFlow === "share" && canUsePartnerInventory && (
          <section className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
            <div className="flex items-center justify-between gap-3">
              <div>
                <h2 className="text-base font-black text-slate-950">Host Approval Queue</h2>
                <p className="mt-1 text-sm text-slate-500">Approve inventory offered into your network.</p>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" disabled={offeredListingIds.length === 0} onClick={() => decideListings("rejected")}><XCircle size={15} /> Reject Offered</Button>
                <Button disabled={offeredListingIds.length === 0} onClick={() => decideListings("approved")}><CheckCircle2 size={15} /> Approve Offered</Button>
              </div>
            </div>
            <ListingGrid listings={hostListings} side="host" onDecision={decideListings} onCommissionChange={updateListingCommission} />
          </section>
          )}

          {activeFlow === "use" && canShareInventory && (
            <section className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
              <h2 className="text-base font-black text-slate-950">Owner Usage Requests</h2>
              <p className="mt-1 text-sm text-slate-500">Approve usage of your hoardings. Client and proposal names are hidden.</p>
              <UsageList usages={pendingOwnerUsages.length ? pendingOwnerUsages : ownerUsages} side="owner" busy={busy} onDecision={decideUsage} />
            </section>
          )}

          {activeFlow === "share" && canUsePartnerInventory && (
            <section className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
              <h2 className="text-base font-black text-slate-950">Host Usage Status</h2>
              <p className="mt-1 text-sm text-slate-500">Track approvals needed before campaign creation.</p>
              <UsageList usages={hostUsages} side="host" busy={busy} onDecision={decideUsage} />
            </section>
          )}

          {activeFlow === "use" && canShareInventory && (
          <section className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
            <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
              <div>
                <h2 className="text-base font-black text-slate-950">Your Shared Listings</h2>
                <p className="mt-1 text-sm text-slate-500">Change what partner hosts see for your shared hoardings.</p>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <Select value={bulkVisibilityStatus} onValueChange={setBulkVisibilityStatus}>
                  <SelectTrigger className="h-9 w-[190px] bg-white"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {visibilityOptions.map((option) => <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Button disabled={busy === "owner-visibility" || selectedOwnerListingIds.length === 0} onClick={updateSelectedVisibility}>
                  {visibilityIcon(bulkVisibilityStatus)}
                  Update selected
                </Button>
              </div>
            </div>
            <OwnerSharedInventoryTable
              rows={sharedInventoryRows}
              selectedIds={selectedOwnerListingIds}
              selectedCount={selectedSharedRows.length}
              allIds={allSharedRowListingIds}
              onToggle={(ids, checked) => setSelectedOwnerListingIds((cur) => toggleListingIds(cur, ids, checked))}
              onToggleAll={(checked) => setSelectedOwnerListingIds(checked ? allSharedRowListingIds : [])}
            />
          </section>
          )}
        </div>
      )}
    </Layout>
  );
}

function ListingGrid({ listings, side = "owner", onDecision, onCommissionChange }) {
  if (!listings.length) return <div className="mt-4 rounded-lg border border-dashed border-slate-300 p-6 text-center text-sm font-semibold text-slate-500">No listings yet.</div>;
  return (
    <div className="mt-4 grid gap-3 md:grid-cols-2 xl:grid-cols-3">
      {listings.map((l) => (
        <div key={l.id} className="rounded-lg border border-slate-200 p-3">
          <div className="flex items-start justify-between gap-2">
            <div>
              <div className="font-mono text-xs font-black text-slate-500">{l.hoarding_code}</div>
              <div className="mt-1 line-clamp-2 font-bold text-slate-950">{l.name}</div>
              <div className="mt-1 text-xs text-slate-500">{l.area}, {l.city} | {l.width}x{l.height}</div>
            </div>
            <Pill tone={statusTone(l.status)}>{l.status}</Pill>
          </div>
          <div className="mt-3 grid grid-cols-3 gap-2 text-xs">
            <Metric label="Display min" value={inr(l.min_display_charge)} />
            <Metric label="Printing min" value={inr(l.min_printing_charge)} />
            <Metric label="Mounting min" value={inr(l.min_mounting_charge)} />
          </div>
          <div className="mt-3 flex flex-wrap gap-2">
            <Pill tone={l.partner_visibility_status === "available" ? "green" : "amber"}>{l.partner_visibility_status}</Pill>
            <Pill>Printing: {l.printing_responsibility}</Pill>
            <Pill>Mounting: {l.mounting_responsibility}</Pill>
            {side === "host" && <Pill tone="blue">{Number(l.effective_commission_pct || 0)}% host commission</Pill>}
          </div>
          {side === "host" && onCommissionChange && (
            <label className="mt-3 block space-y-1">
              <span className="text-xs font-bold text-slate-600">Hoarding commission override %</span>
              <Input
                type="number"
                min="0"
                max="100"
                defaultValue={l.commission_pct ?? ""}
                placeholder={`${Number(l.effective_commission_pct || 0)}% default`}
                onBlur={(e) => {
                  if (e.target.value.trim() !== "") onCommissionChange(l.id, e.target.value);
                }}
              />
            </label>
          )}
          {onDecision && l.status === "offered" && (
            <div className="mt-3 flex gap-2">
              <Button size="sm" variant="outline" onClick={() => onDecision("rejected", [l.id])}><XCircle size={14} /> Reject</Button>
              <Button size="sm" onClick={() => onDecision("approved", [l.id])}><CheckCircle2 size={14} /> Approve</Button>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

function groupOwnerListings(listings) {
  const byHoarding = new Map();
  listings.forEach((listing) => {
    const key = listing.hoarding_id || listing.id;
    const existing = byHoarding.get(key);
    if (!existing) {
      byHoarding.set(key, {
        ...listing,
        listing_ids: [listing.id],
        hosts: [listing.host_advertiser_id],
        host_count: 1,
        approval_statuses: [listing.status],
      });
      return;
    }
    existing.listing_ids.push(listing.id);
    if (!existing.hosts.includes(listing.host_advertiser_id)) existing.hosts.push(listing.host_advertiser_id);
    if (!existing.approval_statuses.includes(listing.status)) existing.approval_statuses.push(listing.status);
    existing.host_count = existing.hosts.length;
  });
  return Array.from(byHoarding.values());
}

function toggleListingIds(current, ids, checked) {
  if (checked) return [...new Set([...current, ...ids])];
  return current.filter((id) => !ids.includes(id));
}

function visibilityLabel(status) {
  return visibilityOptions.find((option) => option.value === status)?.label || status || "-";
}

function visibilityTone(status) {
  if (status === "available") return "green";
  if (status === "occupied" || status === "maintenance") return "amber";
  if (status === "hidden") return "slate";
  return "slate";
}

function visibilityIcon(status) {
  if (status === "hidden") return <EyeOff size={15} />;
  if (status === "maintenance") return <Wrench size={15} />;
  return <Eye size={15} />;
}

function OwnerSharedInventoryTable({ rows, selectedIds, selectedCount, allIds, onToggle, onToggleAll }) {
  if (!rows.length) return <div className="mt-4 rounded-lg border border-dashed border-slate-300 p-6 text-center text-sm font-semibold text-slate-500">No shared inventory yet.</div>;
  const allSelected = allIds.length > 0 && allIds.every((id) => selectedIds.includes(id));
  return (
    <div className="mt-4 overflow-hidden rounded-lg border border-slate-200">
      <div className="flex items-center justify-between border-b border-slate-200 bg-slate-50 px-4 py-3">
        <label className="flex items-center gap-2 text-sm font-bold text-slate-700">
          <input
            type="checkbox"
            className="h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
            checked={allSelected}
            onChange={(e) => onToggleAll(e.target.checked)}
          />
          Select all
        </label>
        <div className="text-xs font-bold text-slate-500">{selectedCount} selected</div>
      </div>
      <div className="divide-y divide-slate-100">
        {rows.map((row) => {
          const selected = row.listing_ids.some((id) => selectedIds.includes(id));
          return (
            <div key={row.hoarding_id || row.id} className="grid gap-3 px-4 py-3 md:grid-cols-[minmax(0,1fr)_120px_140px_140px] md:items-center">
              <label className="flex min-w-0 items-start gap-3">
                <input
                  type="checkbox"
                  className="mt-1 h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                  checked={selected}
                  onChange={(e) => onToggle(row.listing_ids, e.target.checked)}
                />
                <span className="min-w-0">
                  <span className="block truncate font-bold text-slate-950">{row.name}</span>
                  <span className="mt-1 block truncate text-xs font-semibold text-slate-500">{row.hoarding_code} · {row.area}, {row.city} · {row.width}x{row.height}</span>
                </span>
              </label>
              <div className="text-xs font-bold text-slate-500">{row.host_count} host{row.host_count === 1 ? "" : "s"}</div>
              <Pill tone={visibilityTone(row.partner_visibility_status)}>{visibilityLabel(row.partner_visibility_status)}</Pill>
              <Pill tone={row.approval_statuses.includes("approved") ? "green" : statusTone(row.approval_statuses[0])}>{row.approval_statuses.join(", ")}</Pill>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function PartnershipGrid({ partnerships, side, busy, onDecision, onCommissionChange }) {
  if (!partnerships.length) {
    return <div className="mt-4 rounded-lg border border-dashed border-slate-300 p-6 text-center text-sm font-semibold text-slate-500">No partner connections yet.</div>;
  }
  return (
    <div className="mt-4 grid gap-3 md:grid-cols-2 xl:grid-cols-3">
      {partnerships.map((p) => {
        const name = side === "host" ? p.owner_name : p.host_name;
        const email = side === "host" ? p.owner_email : p.host_email;
        return (
          <div key={p.id} className="rounded-lg border border-slate-200 p-3">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="truncate font-bold text-slate-950">{name}</div>
                <div className="mt-1 truncate text-xs font-semibold text-slate-500">{email}</div>
              </div>
              <Pill tone={statusTone(p.status)}>{p.status}</Pill>
            </div>
            <div className="mt-3 text-xs font-semibold text-slate-500">
              {side === "host" ? "Inventory owner" : "Network host"} connection
            </div>
            {side === "host" && onCommissionChange && (
              <label className="mt-3 block space-y-1">
                <span className="text-xs font-bold text-slate-600">Advertiser commission %</span>
                <Input
                  type="number"
                  min="0"
                  max="100"
                  defaultValue={p.commission_pct ?? ""}
                  placeholder="Use default"
                  onBlur={(e) => {
                    if (e.target.value.trim() !== "") onCommissionChange(p.id, e.target.value);
                  }}
                  disabled={busy === `partnership-${p.id}-commission`}
                />
              </label>
            )}
            {side === "host" && p.status === "pending" && onDecision && (
              <div className="mt-3 flex gap-2">
                <Button size="sm" variant="outline" disabled={busy === `partnership-${p.id}-rejected`} onClick={() => onDecision(p.id, "rejected")}><XCircle size={14} /> Reject</Button>
                <Button size="sm" disabled={busy === `partnership-${p.id}-approved`} onClick={() => onDecision(p.id, "approved")}><CheckCircle2 size={14} /> Approve</Button>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function UsageList({ usages, side, busy, onDecision }) {
  if (!usages.length) return <div className="mt-4 rounded-lg border border-dashed border-slate-300 p-6 text-center text-sm font-semibold text-slate-500">No usage requests.</div>;
  return (
    <div className="mt-4 grid gap-3">
      {usages.map((u) => (
        <div key={u.id} className="rounded-lg border border-slate-200 p-3">
          <div className="flex items-start justify-between gap-2">
            <div>
              <div className="font-mono text-xs font-black text-slate-500">{u.hoarding_code}</div>
              <div className="mt-1 font-bold text-slate-950">{u.hoarding_name}</div>
              <div className="mt-1 text-xs text-slate-500">{u.duration_days} days | {u.start_date?.slice?.(0, 10) || "-"} to {u.end_date?.slice?.(0, 10) || "-"}</div>
            </div>
            <Pill tone={statusTone(u.status)}>{u.status}</Pill>
          </div>
          <div className="mt-3 grid grid-cols-2 gap-2 text-xs md:grid-cols-4">
            <Metric label="Display" value={inr(u.owner_display_amount)} />
            <Metric label="Total owner amount" value={inr(u.owner_total_amount)} />
            <Metric label="Monthly display equiv." value={inr(u.monthly_equivalent_display)} />
            <Metric label="Monthly total equiv." value={inr(u.monthly_equivalent_total)} />
          </div>
          <div className="mt-2 text-xs font-semibold text-slate-500">Commission retained by host: {inr(u.commission_amount)} at {Number(u.commission_pct || 0)}%</div>
          {side === "owner" && u.status === "pending" && (
            <div className="mt-3 flex gap-2">
              <Button size="sm" variant="outline" disabled={busy === `${u.id}-rejected`} onClick={() => onDecision(u.id, "rejected")}><XCircle size={14} /> Reject</Button>
              <Button size="sm" disabled={busy === `${u.id}-approved`} onClick={() => onDecision(u.id, "approved")}><CheckCircle2 size={14} /> Approve</Button>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

function Metric({ label, value }) {
  return (
    <div className="rounded-md bg-slate-50 p-2">
      <div className="text-[10px] font-black uppercase text-slate-500">{label}</div>
      <div className="mt-1 font-bold text-slate-950">{value}</div>
    </div>
  );
}
